import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import '../scss/matches.scss';
import Header from '../Header';
import MatchCard from './match/MatchCard';
import ProFeatureModal from './ProFeatureModal';
import api from '../services/apiService';
import { authService } from '../services/authService';

const Matches = () => {
    const [matches, setMatches] = useState([]);
    const [showPastMatches, setShowPastMatches] = useState(false); // false = upcoming, true = past
    const [isLoading, setIsLoading] = useState(true);
    const [showProModal, setShowProModal] = useState(false);
    const [isPro, setIsPro] = useState(false);
    const [showDummyData, setShowDummyData] = useState(false);
    const [totalPlayers, setTotalPlayers] = useState(0);
    const [hasTeamTrainingSessions, setHasTeamTrainingSessions] = useState(false);
    const navigate = useNavigate();
    const playerId = sessionStorage.getItem('player_id');
    const teamId = sessionStorage.getItem('selected_team_id');
    const teamType = sessionStorage.getItem('selected_team_type');
    const isAcademyTeam = teamType === 'academy';
    const isManager = authService.isManager();
    const isParent = authService.isParent();

    // Dummy data functions
    const getDummyFixture = () => {
        // Create an array of dummy matches
        const matches = [];
        
        // Get current date
        const today = new Date();
        
        // Generate past matches (last 1 week)
        for (let week = 1; week <= 1; week++) {
            // Saturday match (day 6 of week, 0-indexed) - in the past
            const pastSaturdayDate = new Date(today);
            pastSaturdayDate.setDate(today.getDate() - (week * 7));
            
            // Different opposition teams for variety
            const oppositionTeams = ['FC United', 'City Rovers', 'Athletic Club', 'United Stars'];
            const locations = ['Home Ground', 'Away Pitch', 'Community Stadium', 'Sports Complex'];
            
            // Add scores for past matches
            const scores = [
                { white: 3, colour: 2 },
                { white: 1, colour: 1 },
                { white: 4, colour: 0 },
                { white: 2, colour: 3 }
            ];
            
            matches.push({
                match_id: 1, // Use match_id: 1 for proper routing to /match/1/result
                type: 'match',
                match_date: format(pastSaturdayDate, 'yyyy-MM-dd'),
                match_time: '14:00:00',
                location: locations[(week - 1) % locations.length],
                max_players: 12,
                yes: [],
                no: [],
                response: 'Yes',
                entry_open: false,
                opposition: oppositionTeams[(week - 1) % oppositionTeams.length],
                isDummy: true,
                // Add score data for past matches
                score_white: scores[(week - 1) % scores.length].white,
                score_colour: scores[(week - 1) % scores.length].colour,
                played_match: true
            });
        }
        
        // Generate future matches for the next 4 weeks (Saturdays)
        for (let week = 1; week <= 4; week++) {
            // Saturday match (day 6 of week, 0-indexed)
            const saturdayDate = new Date(today);
            saturdayDate.setDate(today.getDate() + (6 - today.getDay() + 7) % 7 + (week * 7));
            
            // Different opposition teams for variety
            const oppositionTeams = ['FC United', 'City Rovers', 'Athletic Club', 'United Stars'];
            const locations = ['Home Ground', 'Away Pitch', 'Community Stadium', 'Sports Complex'];
            
            matches.push({
                match_id: `match-saturday-${week}`,
                type: 'match',
                match_date: format(saturdayDate, 'yyyy-MM-dd'),
                match_time: '14:00:00',
                location: locations[(week - 1) % locations.length],
                max_players: 12,
                yes: [],
                no: [],
                response: 'Invited',
                entry_open: true,
                opposition: oppositionTeams[(week - 1) % oppositionTeams.length],
                isDummy: true,
                // Add default score data for future matches (will be null/undefined until played)
                score_white: null,
                score_colour: null,
                played_match: false
            });
        }
        
        return matches;
    };

    const getDummyTraining = () => {
        // Create an array of dummy training sessions
        const trainingSessions = [];
        
        // Get current date
        const today = new Date();
        
        // Generate training sessions for the next 4 weeks (Tuesdays and Thursdays)
        for (let week = 0; week < 4; week++) {
            // Tuesday training (day 2 of week, 0-indexed)
            const tuesdayDate = new Date(today);
            tuesdayDate.setDate(today.getDate() + (2 - today.getDay() + 7) % 7 + (week * 7));
            
            // Thursday training (day 4 of week, 0-indexed)
            const thursdayDate = new Date(today);
            thursdayDate.setDate(today.getDate() + (4 - today.getDay() + 7) % 7 + (week * 7));
            
            // Add Tuesday training
            trainingSessions.push({
                training_id: `training-tuesday-${week + 1}`,
                match_id: `training-tuesday-${week + 1}`,
                match_date: format(tuesdayDate, 'yyyy-MM-dd'),
                match_time: '18:00:00',
                time: '18:00:00',
                location: 'Claverham Community College',
                notes: 'Regular Tuesday training session',
                response: 'Invited',
                total_yes: 7,
                total_no: 2,
                total_awaiting: 3,
                duration: 90,
                isDummy: true
            });
            
            // Add Thursday training
            trainingSessions.push({
                training_id: `training-thursday-${week + 1}`,
                match_id: `training-thursday-${week + 1}`,
                match_date: format(thursdayDate, 'yyyy-MM-dd'),
                match_time: '18:00:00',
                time: '18:00:00',
                location: 'Claverham Community College',
                notes: 'Regular Thursday training session',
                response: 'Invited',
                total_yes: 8,
                total_no: 1,
                total_awaiting: 2,
                duration: 90,
                isDummy: true
            });
        }
        
        return trainingSessions;
    };

    useEffect(() => {
        const fetchPlayerProfile = async () => {
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/get_player_profile.php?player_id=${playerId}`);
                if (response.data.success) {
                    setIsPro(response.data.player?.is_pro || false);
                }
            } catch (error) {
                console.error('Error fetching player profile:', error);
            }
        };

        fetchPlayerProfile();
    }, [playerId]);

    useEffect(() => {
        const fetchTotalPlayers = async () => {
            try {
                if (!teamId || !isManager) return;
                
                const response = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=players`);
                if (response.data.success) {
                    setTotalPlayers(response.data.total_players || 0);
                }
            } catch (error) {
                console.error('Error fetching total players:', error);
            }
        };

        fetchTotalPlayers();
    }, [teamId, isManager]);

    useEffect(() => {
        const fetchTrainingSessions = async () => {
            try {
                if (!teamId || !isManager) return;
                const teamHasTraining = sessionStorage.getItem('selected_team_has_training');
                if (teamHasTraining !== 'true' && teamHasTraining !== '1') return;

                const response = await api.get(
                    `${process.env.REACT_APP_API_URL}/get_training_sessions.php?team_id=${teamId}`
                );
                if (response.data.success && Array.isArray(response.data.sessions) && response.data.sessions.length > 0) {
                    setHasTeamTrainingSessions(true);
                    return;
                }

                try {
                    const cached = sessionStorage.getItem('training_sessions');
                    if (cached) {
                        const sessions = JSON.parse(cached);
                        if (Array.isArray(sessions) && sessions.length > 0) {
                            setHasTeamTrainingSessions(true);
                        }
                    }
                } catch (e) {
                    // Ignore cache parse errors
                }
            } catch (error) {
                console.error('Error fetching training sessions:', error);
            }
        };

        fetchTrainingSessions();
    }, [teamId, isManager]);

    useEffect(() => {
        const fetchMatches = async () => {
            try {
                let apiUrl;
                
                // Different API calls based on user type
                if (isManager) {
                    // Coaches/Managers: Show all matches for their team
                    apiUrl = `${process.env.REACT_APP_API_URL}/get_matches.php?player_id=${playerId}&view_mode=team&team_id=${teamId}&show_future=1&type=all`;
                } else {
                    // Players and Parents: Show only invited matches
                    apiUrl = `${process.env.REACT_APP_API_URL}/get_matches.php?player_id=${playerId}&view_mode=my&team_id=${teamId}&show_future=1&type=all`;
                }
                
                const response = await api.get(apiUrl);
                const result = response.data;
                if (result.success) {
                    const fetchedMatches = result.data || [];
                    
                    // Show real data if available, otherwise show dummy data for non-parents
                    if (fetchedMatches.length > 0) {
                        setMatches(fetchedMatches);
                        setShowDummyData(false);
                    } else if (!isParent) {
                        console.log('No real matches found, showing dummy data');
                        const dummyMatches = getDummyFixture();
                        setMatches(dummyMatches);
                        setShowDummyData(true);
                    } else {
                        setMatches([]);
                        setShowDummyData(false);
                    }
                } else {
                    console.error('Failed to fetch matches:', result);
                    if (!isParent) {
                        const dummyMatches = getDummyFixture();
                        setMatches(dummyMatches);
                        setShowDummyData(true);
                    } else {
                        setMatches([]);
                        setShowDummyData(false);
                    }
                }
            } catch (error) {
                console.error('Error fetching matches:', error);
                if (!isParent) {
                    const dummyMatches = getDummyFixture();
                    setMatches(dummyMatches);
                    setShowDummyData(true);
                } else {
                    setMatches([]);
                    setShowDummyData(false);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchMatches();
    }, [playerId, teamId, isManager, isParent]);

    const handleMatchClick = (matchId, index) => {
        if (!isPro && !isManager && !isParent && index > 0) {
            setShowProModal(true);
            return;
        }
        
        // Check if this is a past match by finding it in the pastMatches array
        const isPastMatch = pastMatches.some(match => match.match_id === matchId);
        
        if (isPastMatch) {
            // Route past matches to the result page
            navigate(`/match/${matchId}/result`);
        } else {
            // Route future matches to the regular match page
            navigate(`/match/${matchId}`);
        }
    };

    const handleMatchDeleted = (matchId) => {
        setMatches(currentMatches => currentMatches.filter(match => match.match_id !== matchId));
    };

    const handleRespond = (matchId, response) => {
        // Implement the logic to respond to a match
        console.log(`Responding to match ${matchId} with response: ${response}`);
    };

    if (isLoading) return <div className="loading">Loading fixtures...</div>;

    // Get current date and time for comparison
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight for date comparison only
    
    // More accurate function to determine if a match is in the future
    const isFutureMatch = (match) => {
        const matchDate = new Date(match.match_date);
        matchDate.setHours(0, 0, 0, 0); // Set to midnight for date comparison
        
        console.log(`Checking match ${match.match_id}: ${match.match_date} vs today ${today.toISOString().split('T')[0]}`);
        
        // If match is today, check the time
        if (matchDate.getTime() === today.getTime() && match.match_time) {
            // Check if this is a TBC match (status is TBC or time is 00:00:00)
            const isTBC = match.status === 'TBC' || match.match_time === '00:00:00' || match.match_time === '00:00';
            
            if (isTBC) {
                // For TBC matches, consider them future if they're today or later
                console.log(`Match ${match.match_id} is TBC match today - treating as future`);
                return true;
            }
            
            // Parse the match time (format: "HH:MM:SS" or "HH:MM")
            const [hours, minutes] = match.match_time.split(':').map(Number);
            const matchDateTime = new Date(matchDate);
            matchDateTime.setHours(hours, minutes, 0, 0);
            
            // Add a buffer of 30 minutes after match time to account for match duration
            // Assuming each match lasts about 90 minutes
            const matchEndTime = new Date(matchDateTime);
            matchEndTime.setMinutes(matchEndTime.getMinutes() + 120); // 2 hours match buffer
            
            const isFuture = matchEndTime > now;
            console.log(`Match ${match.match_id} is today, end time: ${matchEndTime.toLocaleString()}, now: ${now.toLocaleString()}, isFuture: ${isFuture}`);
            return isFuture; // Match is in the future if end time is later than now
        }
        
        // For non-today matches, just compare dates
        const isFuture = matchDate > today;
        console.log(`Match ${match.match_id} is not today, matchDate: ${matchDate.toISOString().split('T')[0]}, today: ${today.toISOString().split('T')[0]}, isFuture: ${isFuture}`);
        return isFuture;
    };

    // Filter matches into future and past
    const futureMatches = matches.filter(isFutureMatch);
    const pastMatches = matches.filter(match => !isFutureMatch(match));
    
    console.log('=== MATCH CLASSIFICATION ===');
    console.log('Future matches:', futureMatches.map(m => `${m.match_id}: ${m.match_date}`));
    console.log('Past matches:', pastMatches.map(m => `${m.match_id}: ${m.match_date}`));
    console.log('===========================');
    
    // Sort future matches in ascending order (nearest first)
    futureMatches.sort((a, b) => {
        // First by date
        const dateA = new Date(a.match_date);
        const dateB = new Date(b.match_date);
        if (dateA.getTime() !== dateB.getTime()) {
            return dateA - dateB;
        }
        // Then by time if dates are the same
        // Handle TBC matches by putting them after regular matches
        const aIsTBC = a.status === 'TBC' || a.match_time === '00:00:00' || a.match_time === '00:00';
        const bIsTBC = b.status === 'TBC' || b.match_time === '00:00:00' || b.match_time === '00:00';
        
        if (aIsTBC && !bIsTBC) return 1; // TBC matches go after regular matches
        if (!aIsTBC && bIsTBC) return -1; // Regular matches go before TBC matches
        if (aIsTBC && bIsTBC) return 0; // Both TBC, maintain order
        
        return a.match_time.localeCompare(b.match_time);
    });
    
    // Sort past matches in descending order (most recent first)
    pastMatches.sort((a, b) => {
        // First by date
        const dateA = new Date(a.match_date);
        const dateB = new Date(b.match_date);
        if (dateA.getTime() !== dateB.getTime()) {
            return dateB - dateA;
        }
        // Then by time if dates are the same
        // Handle TBC matches by putting them after regular matches
        const aIsTBC = a.status === 'TBC' || a.match_time === '00:00:00' || a.match_time === '00:00';
        const bIsTBC = b.status === 'TBC' || b.match_time === '00:00:00' || b.match_time === '00:00';
        
        if (aIsTBC && !bIsTBC) return 1; // TBC matches go after regular matches
        if (!aIsTBC && bIsTBC) return -1; // Regular matches go before TBC matches
        if (aIsTBC && bIsTBC) return 0; // Both TBC, maintain order
        
        return b.match_time.localeCompare(a.match_time);
    });

    console.log(`Now: ${now.toLocaleString()}`);
    console.log(`Today: ${today.toISOString().split('T')[0]}`);
    console.log(`Total matches: ${matches.length}`);
    console.log(`Future matches: ${futureMatches.length}`);
    console.log(`Past matches: ${pastMatches.length}`);

    return (
        <div className="matches-container container">
            <Header />
            <h2 className="audiowide">Fixtures</h2>
            
            {showDummyData && (
                <div className="dummy-data-notice global-notice">
                    <h4>Welcome to FirstWhistle!</h4>
                    <p>We're showing example fixtures and training sessions to help you experience the app.
                    Your upcoming and past fixtures will appear here once they've been scheduled by your coach.</p>
                </div>
            )}
            
            <div className="filters">
                <div className="fixtures-filter">
                    <button 
                        className={`filter-button ${!showPastMatches ? 'active' : ''}`}
                        onClick={() => setShowPastMatches(false)}
                    >
                        Upcoming Fixtures
                    </button>
                    <button 
                        className={`filter-button ${showPastMatches ? 'active' : ''}`}
                        onClick={() => setShowPastMatches(true)}
                    >
                        Previous Fixtures
                    </button>
                </div>

                {/* Create Match button for managers/coaches */}
                {(isManager || authService.isAdmin()) && !showPastMatches && (isAcademyTeam || totalPlayers >= 6 || hasTeamTrainingSessions) && (
                    <div className="create-match-section">
                        <button 
                            className="create-match-button"
                            onClick={() => navigate('/createMatch')}
                        >
                            Create Match
                        </button>
                    </div>
                )}
            </div>

            {!showPastMatches && futureMatches.length > 0 && (
                <>
                    <h2 className="matches-heading">Upcoming Fixtures</h2>
                    <div className="matches-list">
                        {futureMatches.map((match, index) => (
                            <div key={match.match_id} className="match-wrapper">
                                <MatchCard
                                    match={{
                                        ...match,
                                        id: match.match_id,
                                        date: match.match_date,
                                        time: match.match_time,
                                        location: match.location,
                                        response: match.response,
                                        counts: match.counts,
                                        maxPlayers: match.max_players,
                                        totalYes: match.total_yes,
                                        active_yes: match.active_yes || parseInt(match.total_yes) || 0,
                                        waitlist_yes: match.waitlist_yes || 0,
                                        active_player_ids: match.active_player_ids || '',
                                        waitlist_player_ids: match.waitlist_player_ids || '',
                                        total_awaiting: match.total_awaiting || 0,
                                        match_date: match.match_date,
                                        entry_open: match.entry_open,
                                        opposition: match.opposition,
                                        team_name: match.team_name,
                                        is_home: match.is_home,
                                        motm_names: match.motm_names,
                                        onRespond: (response) => handleRespond(match.match_id, response)
                                    }}
                                    onMatchDeleted={handleMatchDeleted}
                                    onClick={() => handleMatchClick(match.match_id, index)}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}

            {showPastMatches && pastMatches.length > 0 && (
                <>
                    <h2 className="matches-heading">Previous Fixtures</h2>
                    <div className="matches-list">
                        {pastMatches.map((match, index) => (
                            <div key={match.match_id} className="match-wrapper">
                                <MatchCard
                                    match={{
                                        ...match,
                                        id: match.match_id,
                                        date: match.match_date,
                                        time: match.match_time,
                                        location: match.location,
                                        response: match.response,
                                        counts: match.counts,
                                        maxPlayers: match.max_players,
                                        totalYes: match.total_yes,
                                        active_yes: match.active_yes || parseInt(match.total_yes) || 0,
                                        waitlist_yes: match.waitlist_yes || 0,
                                        active_player_ids: match.active_player_ids || '',
                                        waitlist_player_ids: match.waitlist_player_ids || '',
                                        total_awaiting: match.total_awaiting || 0,
                                        match_date: match.match_date,
                                        entry_open: match.entry_open,
                                        opposition: match.opposition,
                                        team_name: match.team_name,
                                        is_home: match.is_home,
                                        motm_names: match.motm_names,
                                        onRespond: (response) => handleRespond(match.match_id, response)
                                    }}
                                    onMatchDeleted={handleMatchDeleted}
                                    onClick={() => handleMatchClick(match.match_id, index)}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}

            {((showPastMatches && pastMatches.length === 0) || 
              (!showPastMatches && futureMatches.length === 0)) && (
                <div className="no-matches">
                    <p>No {showPastMatches ? 'previous' : 'upcoming'} fixtures scheduled</p>
                </div>
            )}

            <ProFeatureModal
                isOpen={showProModal}
                onClose={() => setShowProModal(false)}
            />
        </div>
    );
};

export default Matches; 